import type { ComplianceScore } from "@/lib/api/types";
import { DEFAULT_PREFERRED_HOST, type PreferredHost } from "@spectree/shared";
import {
  buildImproveEpicCommand,
  buildPlannerFromRequestCommand,
  buildOrchestratorCommand,
  getAgentPromptExplanation,
} from "@/lib/chat/cli-guidance";

// ---------------------------------------------------------------------------
// Pipeline stage types
// ---------------------------------------------------------------------------

export type PipelineStage =
  | "no-requests"
  | "has-pending-request"
  | "has-approved-request"
  | "has-epic"
  | "epic-in-progress"
  | "epic-complete";

export interface PipelineInfo {
  stage: PipelineStage;
  title: string;
  description: string;
  command: string | null;
  commandLabel: string | null;
  commandHelperText: string | null;
  sproutPrompt: string | null;
}

// ---------------------------------------------------------------------------
// Epic Request stage detection
// ---------------------------------------------------------------------------

export function detectEpicRequestStage(
  request: {
    status: string;
    title: string;
    identifier?: string | null | undefined;
    convertedEpicId?: string | null | undefined;
  },
  host: PreferredHost = DEFAULT_PREFERRED_HOST,
): PipelineInfo {
  switch (request.status) {
    case "pending":
      return {
        stage: "has-pending-request",
        title: "Request Submitted",
        description: "Your request is awaiting review.",
        command: null,
        commandLabel: null,
        commandHelperText: null,
        sproutPrompt: `Tell me about epic request "${request.title}"`,
      };

    case "approved":
      return {
        stage: "has-approved-request",
        title: "Ready to Plan",
        description:
          "Your request has been approved! Plan it into an epic.",
        command: buildPlannerFromRequestCommand(request.title, host),
        commandLabel: "Plan this request",
        commandHelperText: getAgentPromptExplanation(host),
        sproutPrompt: `Help me plan approved request "${request.title}" into an epic`,
      };

    case "planning":
      return {
        stage: "has-approved-request",
        title: "Planning in Progress",
        description: "Your request is being planned.",
        command: null,
        commandLabel: null,
        commandHelperText: null,
        sproutPrompt: null,
      };

    case "converted":
      return {
        stage: "has-epic",
        title: "Epic Created",
        description: "This request has been converted into an epic.",
        command: null,
        commandLabel: null,
        commandHelperText: null,
        sproutPrompt: null,
      };

    case "rejected":
      return {
        stage: "no-requests",
        title: "Request Rejected",
        description: "This request was not approved.",
        command: null,
        commandLabel: null,
        commandHelperText: null,
        sproutPrompt: null,
      };

    default:
      return {
        stage: "no-requests",
        title: "Unknown Status",
        description: "Unable to determine the current stage.",
        command: null,
        commandLabel: null,
        commandHelperText: null,
        sproutPrompt: null,
      };
  }
}

// ---------------------------------------------------------------------------
// Epic stage detection
// ---------------------------------------------------------------------------

export function detectEpicStage(
  epic: {
    identifier: string;
    name: string;
    state?: string | null;
    featureCount?: number;
  },
  hasActiveSession: boolean,
  complianceScore?: Pick<ComplianceScore, "overall" | "tier"> | null,
  host: PreferredHost = DEFAULT_PREFERRED_HOST,
): PipelineInfo {
  if (epic.state === "implemented") {
    return {
      stage: "epic-complete",
      title: "Epic Complete",
      description: "All features implemented!",
      command: null,
      commandLabel: null,
      commandHelperText: null,
      sproutPrompt: null,
    };
  }

  if (hasActiveSession) {
    return {
      stage: "epic-in-progress",
      title: "Execution Active",
      description: "An orchestrator session is running.",
      command: null,
      commandLabel: null,
      commandHelperText: null,
      sproutPrompt: null,
    };
  }

  if (epic.featureCount && epic.featureCount > 0) {
    if (!complianceScore || complianceScore.overall < 90) {
      return {
        stage: "has-epic",
        title: "Improve Score to Execute",
        description:
          "This epic needs a compliance score of 90+ before the orchestrator can execute it.",
        command: buildImproveEpicCommand(epic.identifier, host),
        commandLabel: "Improve this epic",
        commandHelperText: getAgentPromptExplanation(host),
        sproutPrompt: null,
      };
    }

    return {
      stage: "has-epic",
      title: "Ready to Execute",
      description: "This epic has features ready for execution.",
      command: buildOrchestratorCommand(epic.identifier, host),
      commandLabel: "Execute this epic",
      commandHelperText: getAgentPromptExplanation(host),
      sproutPrompt: null,
    };
  }

  return {
    stage: "has-epic",
    title: "Epic Created",
    description: "This epic needs features to be planned.",
    command: null,
    commandLabel: null,
    commandHelperText: null,
    sproutPrompt: null,
  };
}
